import type { ComponentProps, RefObject } from 'react';
import React, { useCallback } from 'react';
import type { HostInstance } from 'react-native';

import { getLocationOrderChannel } from '../../../../../../helpers';
import { useLocationSearchHelpers } from '../../../../../../hooks/useLocationSearchHelpers';
import { type Location, type LocationSearchService } from '../../../../../../machine';
import { LocationResultsCard } from '../../../LocationResultCard';
import { type LocationResultsListsV2Props } from '../../LocationResultsListsV2.types';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListsV2Card = (props: LocationResultsListsV2LocationCardProps) => {
  const {
    locationSearchActorRef,
    variation = 'standard',
    location,
    shouldShowLocationOrderChannel,
    interactedLocationDeliveryAddressId,
    interactedLocationRestaurantSlug,
    onLayout,
    onUmount,
  } = props;

  const {
    navigateToSelectedLocation,
    navigateToSelectedDeliveryLocation,
    navigateToRecentOrNearbyLocation,
    setFocusedLocationPin,
  } = useLocationSearchHelpers({ locationSearchActorRef });

  // ─── Derived Data ────────────────────────────────────────────────────

  const locationOrderChannel = getLocationOrderChannel(location);
  const locationRestaurantSlug = location.restaurantSlug;
  const locationDeliveryAddressId = location.addressId;
  const isDeliveryLocation = locationDeliveryAddressId !== undefined;

  const isReordering = isDeliveryLocation
    ? locationDeliveryAddressId === interactedLocationDeliveryAddressId
    : locationRestaurantSlug === interactedLocationRestaurantSlug;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleCardPress = useCallback<OnLocationResultsCardPress>(
    (_, options) => {
      if (variation === 'recent-and-nearby') {
        navigateToRecentOrNearbyLocation({ location, options });

        return;
      }

      if (locationOrderChannel === 'delivery') {
        navigateToSelectedDeliveryLocation(location, options);

        return;
      }

      navigateToSelectedLocation(location, options);
    },
    [
      location,
      locationOrderChannel,
      navigateToRecentOrNearbyLocation,
      navigateToSelectedDeliveryLocation,
      navigateToSelectedLocation,
      variation,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <LocationResultsCard
      locationSearchActorRef={locationSearchActorRef}
      withoutOrderNowBtn={variation === 'recent-and-nearby'}
      location={location}
      shouldShowLocationOrderChannel={shouldShowLocationOrderChannel}
      key={location.id}
      onPress={handleCardPress}
      onLayout={onLayout}
      onFocus={setFocusedLocationPin}
      onUmount={onUmount}
      withDetailsModal={locationOrderChannel !== 'delivery'}
      isDisabled={isReordering}
      isLoading={isReordering}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsListsV2LocationCardProps = {
  variation?: LocationResultsListsV2Props['variation'];
  location: Location;
  shouldShowLocationOrderChannel: boolean;
  interactedLocationRestaurantSlug?: string;
  interactedLocationDeliveryAddressId?: string;
  locationSearchActorRef: LocationSearchService;
  onLayout?: (listItemRef: RefObject<HostInstance | null>, listItemId: string) => void;
  onUmount?: (id: Location['id']) => void;
};

type OnLocationResultsCardPress = LocationResultsCardProps['onPress'];

type LocationResultsCardProps = ComponentProps<typeof LocationResultsCard>;
